// ── Ping/Communication Wheel (Apex-style) ──
// Radial wheel with 8 segments for team communication.
// Tap for quick ping, hold > 300ms for radial selection.

/** All available ping types. */
export type PingType =
  | 'enemy'
  | 'going'
  | 'backup'
  | 'affirmative'
  | 'negative'
  | 'rush'
  | 'hold'
  | 'save';

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface PingDef {
  label: string;
  color: string;
  icon: string;
}

/**
 * Definitions for each ping type.
 * Segments are ordered clockwise from the top (north):
 *   0=enemy (top), 1=going (top-right), 2=backup (right), 3=affirmative (bottom-right),
 *   4=negative (bottom), 5=rush (bottom-left), 6=hold (left), 7=save (top-left)
 */
export const PING_DEFS: Record<PingType, PingDef> = {
  enemy:       { label: 'Enemy spotted',  color: '#ff4444', icon: '⚠' },
  going:       { label: 'Going here',     color: '#44ff44', icon: '➤' },
  backup:      { label: 'Need backup',    color: '#ffaa00', icon: '🆘' },
  affirmative: { label: 'Affirmative',    color: '#44aaff', icon: '✓' },
  negative:    { label: 'Negative',       color: '#ff6666', icon: '✗' },
  rush:        { label: 'Rush B / Rush A', color: '#ff8800', icon: '⚡' },
  hold:        { label: 'Hold position',  color: '#aaaaff', icon: '🛡' },
  save:        { label: 'Save (eco)',     color: '#ffff44', icon: '$' },
};

/** Ordered list of segments, clockwise from top (8-segment full mode). */
const SEGMENT_ORDER: PingType[] = [
  'enemy',       // 0 — top
  'going',       // 1 — top-right
  'backup',      // 2 — right
  'affirmative', // 3 — bottom-right
  'negative',    // 4 — bottom
  'rush',        // 5 — bottom-left
  'hold',        // 6 — left
  'save',        // 7 — top-left
];

/**
 * Compact-mode segment order (4 cardinals, clockwise from top).
 * Used for simple-control-mode mobile so segments are larger and
 * easier to hit with a thumb. Picks the four most-actioned pings:
 * spot enemies, request backup, deny, and hold.
 */
const SEGMENT_ORDER_COMPACT: PingType[] = [
  'enemy',    // 0 — top
  'backup',   // 1 — right
  'negative', // 2 — bottom
  'hold',     // 3 — left
];

/** Minimum drag distance from center to register a segment (pixels). */
const DEADZONE_RADIUS = 20;

/** Radius (px) from wheel center to each label/segment ring. */
const WHEEL_RADIUS = 80;
/** Size (px) of each segment tile (square) in full 8-segment mode. */
const SEGMENT_SIZE = 72;

/**
 * Radius (px) from wheel center for compact-mode tiles. Pushed out a touch
 * so the larger tiles don't overlap and the wheel reads as a "big 4-button" UI.
 */
const WHEEL_RADIUS_COMPACT = 96;
/**
 * Size (px) of each segment tile in compact (4-segment) mode. Larger touch
 * targets for simple-control-mode users.
 */
const SEGMENT_SIZE_COMPACT = 112;

export class PingWheel {
  private _visible = false;
  private _centerX = 0;
  private _centerY = 0;
  private _selectedIndex = -1;
  private _onPing: (type: PingType, position?: Vec3) => void;
  /** Compact (4-segment) mode for simple-control-mode mobile users. */
  private _compact = false;

  // DOM nodes (only created when a document is available — tests run in node)
  private _root: HTMLDivElement | null = null;
  private _segments: HTMLDivElement[] = [];

  constructor(onPing: (type: PingType, position?: Vec3) => void) {
    this._onPing = onPing;
  }

  /**
   * Toggle compact (4-segment) layout. Compact mode renders fewer, larger
   * tiles so simple-control-mode mobile users (who can't see all 8 pings
   * comfortably) still get a touch-friendly wheel. If the wheel is
   * currently visible, the DOM is re-rendered in place.
   */
  setCompactMode(compact: boolean): void {
    if (this._compact === compact) return;
    this._compact = compact;
    // Drop any in-flight selection — segment indices change between modes.
    this._selectedIndex = -1;
    if (this._visible && typeof document !== 'undefined') {
      // Tear down + remount so segment positions/sizes refresh.
      this._unmountDom();
      this._mountDom();
    }
  }

  /** Whether compact (4-segment) mode is active. */
  isCompact(): boolean {
    return this._compact;
  }

  /** Active segment order based on current mode. */
  private _segmentOrder(): PingType[] {
    return this._compact ? SEGMENT_ORDER_COMPACT : SEGMENT_ORDER;
  }

  /** Show the radial wheel centered at the given screen position. */
  show(x: number, y: number): void {
    this._visible = true;
    this._centerX = x;
    this._centerY = y;
    this._selectedIndex = -1;
    this._mountDom();
  }

  /** Hide the radial wheel without selecting. */
  hide(): void {
    this._visible = false;
    this._selectedIndex = -1;
    this._unmountDom();
  }

  /** Whether the wheel is currently displayed. */
  isVisible(): boolean {
    return this._visible;
  }

  /**
   * Update the highlighted segment based on touch/cursor position.
   * Does NOT select — selection happens on handleTouchEnd.
   */
  handleTouchMove(x: number, y: number): void {
    if (!this._visible) return;

    const dx = x - this._centerX;
    const dy = y - this._centerY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < DEADZONE_RADIUS) {
      this._selectedIndex = -1;
      return;
    }

    // Compute angle from center. atan2(dy, dx) gives angle in radians
    // where 0 = right, positive = down (screen coords).
    // We want 0 = top (north), clockwise.
    // Convert: north-based angle = atan2(dx, -dy)
    let angle = Math.atan2(dx, -dy); // radians, 0 = up, clockwise positive
    if (angle < 0) angle += 2 * Math.PI;

    // Each segment spans an equal slice of the full circle.
    const segmentCount = this._segmentOrder().length;
    const segmentAngle = (2 * Math.PI) / segmentCount;
    // Offset by half a segment so segment 0 is centered at 0 (top)
    const adjusted = (angle + segmentAngle / 2) % (2 * Math.PI);
    this._selectedIndex = Math.floor(adjusted / segmentAngle);

    // Clamp to valid range
    if (this._selectedIndex < 0 || this._selectedIndex >= segmentCount) {
      this._selectedIndex = 0;
    }

    this._refreshHighlight();
  }

  /**
   * Finalize selection on touch release.
   * Returns the selected PingType, or null if wheel wasn't visible.
   * Fires the onPing callback and hides the wheel.
   */
  handleTouchEnd(): PingType | null {
    if (!this._visible) return null;

    const order = this._segmentOrder();
    let selected: PingType;
    if (this._selectedIndex >= 0 && this._selectedIndex < order.length) {
      selected = order[this._selectedIndex];
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate(20);
      }
    } else {
      // Quick ping — no segment selected (tap or near center)
      selected = 'going';
    }

    this._visible = false;
    this._selectedIndex = -1;
    this._unmountDom();
    this._dispatchCallback(selected);
    return selected;
  }

  /** Clean up resources. */
  dispose(): void {
    this._visible = false;
    this._selectedIndex = -1;
    this._unmountDom();
  }

  // ── DOM rendering ────────────────────────────────────────────────────────
  // Guarded so that tests (node env, no `document`) keep working.

  private _mountDom(): void {
    if (typeof document === 'undefined') return;
    // Idempotent: if already mounted, just reposition + refresh.
    if (this._root) {
      this._positionRoot();
      this._refreshHighlight();
      return;
    }

    const root = document.createElement('div');
    root.className = 'cp-ping-wheel';
    root.style.position = 'fixed';
    root.style.left = '0';
    root.style.top = '0';
    root.style.width = '0';
    root.style.height = '0';
    root.style.pointerEvents = 'auto';
    root.style.zIndex = '9999';
    root.style.fontFamily = 'system-ui, sans-serif';

    // Center dot (visual anchor + deadzone indicator)
    const dot = document.createElement('div');
    dot.style.position = 'absolute';
    dot.style.left = '-6px';
    dot.style.top = '-6px';
    dot.style.width = '12px';
    dot.style.height = '12px';
    dot.style.borderRadius = '50%';
    dot.style.background = 'rgba(255,255,255,0.6)';
    dot.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.6)';
    root.appendChild(dot);

    const order = this._segmentOrder();
    const wheelRadius = this._compact ? WHEEL_RADIUS_COMPACT : WHEEL_RADIUS;
    const segmentSize = this._compact ? SEGMENT_SIZE_COMPACT : SEGMENT_SIZE;
    const iconSize = this._compact ? 32 : 20;
    const labelSize = this._compact ? 13 : 10;

    // Ring outline behind the segments
    const ring = document.createElement('div');
    const ringSize = wheelRadius * 2 + segmentSize;
    ring.style.position = 'absolute';
    ring.style.left = `${-ringSize / 2}px`;
    ring.style.top = `${-ringSize / 2}px`;
    ring.style.width = `${ringSize}px`;
    ring.style.height = `${ringSize}px`;
    ring.style.borderRadius = '50%';
    ring.style.background = 'rgba(0,0,0,0.55)';
    ring.style.border = '1px solid rgba(255,255,255,0.15)';
    root.appendChild(ring);

    this._segments = [];
    const angleStep = (2 * Math.PI) / order.length;
    for (let i = 0; i < order.length; i++) {
      const type = order[i];
      const def = PING_DEFS[type];

      // Angle: segment 0 at -90deg (top), then clockwise.
      const angleRad = (-Math.PI / 2) + i * angleStep;
      const x = Math.cos(angleRad) * wheelRadius;
      const y = Math.sin(angleRad) * wheelRadius;

      const seg = document.createElement('div');
      seg.dataset.index = String(i);
      seg.dataset.type = type;
      seg.style.position = 'absolute';
      seg.style.left = `${x - segmentSize / 2}px`;
      seg.style.top = `${y - segmentSize / 2}px`;
      seg.style.width = `${segmentSize}px`;
      seg.style.height = `${segmentSize}px`;
      seg.style.display = 'flex';
      seg.style.flexDirection = 'column';
      seg.style.alignItems = 'center';
      seg.style.justifyContent = 'center';
      seg.style.padding = '4px';
      seg.style.boxSizing = 'border-box';
      seg.style.borderRadius = this._compact ? '14px' : '8px';
      seg.style.background = 'rgba(0,0,0,0.65)';
      seg.style.border = '1px solid rgba(255,255,255,0.18)';
      seg.style.color = '#ffffff';
      seg.style.fontSize = `${labelSize + 1}px`;
      seg.style.textAlign = 'center';
      seg.style.lineHeight = '1.15';
      seg.style.transition = 'transform 80ms ease-out, background 80ms ease-out, border-color 80ms ease-out';

      const icon = document.createElement('div');
      icon.textContent = def.icon;
      icon.style.fontSize = `${iconSize}px`;
      icon.style.lineHeight = '1';
      icon.style.marginBottom = '4px';
      icon.style.color = def.color;
      seg.appendChild(icon);

      const label = document.createElement('div');
      label.textContent = def.label;
      label.style.fontSize = `${labelSize}px`;
      label.style.opacity = '0.85';
      label.style.whiteSpace = 'nowrap';
      label.style.overflow = 'hidden';
      label.style.textOverflow = 'ellipsis';
      label.style.maxWidth = `${segmentSize - 8}px`;
      seg.appendChild(label);

      root.appendChild(seg);
      this._segments.push(seg);
    }

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close ping wheel');
    closeBtn.textContent = 'x';
    closeBtn.style.position = 'absolute';
    closeBtn.style.left = '-8px';
    closeBtn.style.top = `${wheelRadius + segmentSize / 2 + 10}px`;
    closeBtn.style.width = '16px';
    closeBtn.style.height = '16px';
    closeBtn.style.padding = '0';
    closeBtn.style.border = '1px solid rgba(255,255,255,0.28)';
    closeBtn.style.borderRadius = '50%';
    closeBtn.style.background = 'rgba(0,0,0,0.78)';
    closeBtn.style.color = '#ffffff';
    closeBtn.style.font = '700 11px system-ui, sans-serif';
    closeBtn.style.lineHeight = '14px';
    closeBtn.style.textAlign = 'center';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.pointerEvents = 'auto';
    const closeWheel = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this._dismissWithNull();
    };
    closeBtn.addEventListener('click', closeWheel);
    closeBtn.addEventListener('touchend', closeWheel);
    root.appendChild(closeBtn);

    document.body.appendChild(root);
    this._root = root;
    this._positionRoot();
    this._refreshHighlight();
  }

  private _positionRoot(): void {
    if (!this._root) return;
    this._root.style.transform = `translate(${this._centerX}px, ${this._centerY}px)`;
  }

  private _refreshHighlight(): void {
    if (!this._root || this._segments.length === 0) return;
    const order = this._segmentOrder();
    for (let i = 0; i < this._segments.length; i++) {
      const seg = this._segments[i];
      const type = order[i];
      const accent = PING_DEFS[type].color;
      if (i === this._selectedIndex) {
        seg.style.background = 'rgba(255,255,255,0.12)';
        seg.style.border = `1px solid ${accent}`;
        seg.style.transform = 'scale(1.08)';
        seg.style.boxShadow = `0 0 14px ${accent}55`;
      } else {
        seg.style.background = 'rgba(0,0,0,0.65)';
        seg.style.border = '1px solid rgba(255,255,255,0.18)';
        seg.style.transform = 'scale(1)';
        seg.style.boxShadow = 'none';
      }
    }
  }

  private _unmountDom(): void {
    if (!this._root) return;
    if (this._root.parentNode) {
      this._root.parentNode.removeChild(this._root);
    }
    this._root = null;
    this._segments = [];
  }

  private _dismissWithNull(): void {
    if (!this._visible) return;
    this._visible = false;
    this._selectedIndex = -1;
    this._dispatchCallback(null);
    this._unmountDom();
  }

  private _dispatchCallback(type: PingType | null): void {
    if (type === null) return;
    this._onPing(type, undefined);
  }
}
